import { useState, useRef } from 'react'
import { router, Link } from '@inertiajs/react'
import axios from 'axios'
import Layout from '../Layout'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    year:   'numeric',
    month:  'short',
    day:    'numeric',
    hour:   '2-digit',
    minute: '2-digit',
  })
}

const TYPE_COLORS = {
  architecture: 'bg-violet-900/60 text-violet-300',
  bugfix:       'bg-red-900/60 text-red-300',
  decision:     'bg-blue-900/60 text-blue-300',
  discovery:    'bg-amber-900/60 text-amber-300',
  pattern:      'bg-emerald-900/60 text-emerald-300',
  config:       'bg-zinc-700/80 text-zinc-300',
  preference:   'bg-pink-900/60 text-pink-300',
}

function TypeBadge({ type }) {
  const cls = TYPE_COLORS[type] || 'bg-zinc-700/80 text-zinc-300'
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-mono font-medium ${cls}`}>
      {type}
    </span>
  )
}

// DECISION-6 — preview-before-download export. Clicking "Export JSON" fires
// axios.get ?preview=true; a modal shows the counts and (when
// total_dropped_edges > 0) an amber depth-limit notice before the real
// download is triggered via window.location.
// F5 (round 6): try/catch/finally around the preview request — previously a
// rejected request left the button in a silent dead end with no feedback.
function ExportButton({ project }) {
  const [showModal, setShowModal] = useState(false)
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  async function handleExportClick() {
    setPreviewError(null)
    setIsPreviewLoading(true)
    try {
      const response = await axios.get(`/settings/projects/${project.slug}/export?preview=true`)
      setPreview(response.data)
      setShowModal(true)
    } catch (err) {
      setPreviewError('No se pudo generar la vista previa del export. Intentá de nuevo.')
    } finally {
      setIsPreviewLoading(false)
    }
  }

  function handleDownload() {
    window.location.href = `/settings/projects/${project.slug}/export`
    setShowModal(false)
  }

  function handleCancel() {
    setShowModal(false)
    setPreview(null)
  }

  const droppedEdges = preview?.total_dropped_edges ?? 0

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={handleExportClick}
        disabled={isPreviewLoading}
        className="px-3 py-1.5 rounded text-xs font-mono text-emerald-400 border border-emerald-900 hover:bg-emerald-950/60 hover:text-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Export JSON
      </button>

      {previewError && (
        <p role="alert" className="text-xs font-mono text-red-400 max-w-[16rem] text-right">
          {previewError}
        </p>
      )}

      {showModal && preview && (
        <div role="dialog" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-zinc-900 border border-zinc-800 rounded p-4 space-y-3 max-w-sm w-full mx-4">
            <h3 className="text-sm font-mono font-semibold text-zinc-100">Export preview</h3>

            <p className="text-xs font-mono text-zinc-400">
              {preview.items_count} {preview.items_count === 1 ? 'memory' : 'memories'}, {preview.relations_count} relations.
            </p>

            {droppedEdges > 0 && (
              <p
                data-testid="depth-limit-warning"
                className="text-xs font-mono text-amber-400 border border-amber-900 rounded p-2"
              >
                {droppedEdges} relation edge{droppedEdges !== 1 ? 's' : ''} omitted due to depth limit.
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 rounded text-xs font-mono text-zinc-400 border border-zinc-700 hover:bg-zinc-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                className="px-3 py-1.5 rounded text-xs font-mono text-emerald-400 border border-emerald-900 hover:bg-emerald-950/60 hover:text-emerald-300 transition-colors"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Two-step backup import: upload (preflight, writes nothing) then confirm
// (execute). preflight_collisions / preflight_summary / import_summary are
// server-driven Inertia props (see Settings::ProjectsController#show).
function ImportSection({ project, preflight_collisions, preflight_summary, import_summary }) {
  const fileInputRef = useRef(null)

  function handleUpload(e) {
    e.preventDefault()
    const file = fileInputRef.current?.files?.[0]
    if (!file) return

    router.post(
      `/settings/projects/${project.slug}/import`,
      { file },
      { forceFormData: true, preserveScroll: true }
    )
  }

  function handleConfirm(e) {
    e.preventDefault()
    router.post(
      `/settings/projects/${project.slug}/import`,
      { confirm: 'true' },
      { preserveScroll: true }
    )
  }

  return (
    <section className="space-y-3">
      <h2 className="text-sm font-mono font-semibold text-zinc-300">Import backup</h2>

      <form onSubmit={handleUpload} className="flex items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/json"
          className="text-xs font-mono text-zinc-400 file:mr-2 file:px-2 file:py-1 file:rounded file:border file:border-zinc-700 file:bg-zinc-800 file:text-zinc-300"
        />
        <button
          type="submit"
          className="px-3 py-1.5 rounded text-xs font-mono text-emerald-400 border border-emerald-900 hover:bg-emerald-950/60 hover:text-emerald-300 transition-colors"
        >
          Import
        </button>
      </form>

      {preflight_summary && (
        <div className="border border-amber-900 rounded p-3 space-y-2">
          <p className="text-xs font-mono text-zinc-400">
            Pending import: {preflight_summary.items_count} memories, {preflight_summary.relations_count} relations
            across {preflight_summary.pages_count} page{preflight_summary.pages_count !== 1 ? 's' : ''}.
          </p>

          {preflight_collisions && preflight_collisions.length > 0 && (
            <ul className="text-xs font-mono text-amber-400 space-y-0.5 list-disc list-inside">
              {preflight_collisions.map(c => (
                <li key={c.topic_key}>{c.topic_key}</li>
              ))}
            </ul>
          )}

          <form onSubmit={handleConfirm}>
            <button
              type="submit"
              className="px-3 py-1.5 rounded text-xs font-mono text-amber-400 border border-amber-900 hover:bg-amber-950/60 hover:text-amber-300 transition-colors"
            >
              Confirm Import
            </button>
          </form>
        </div>
      )}

      {import_summary && (
        <div className="border border-emerald-900 rounded p-3 space-y-1">
          <p className="text-xs font-mono font-semibold text-emerald-400">Import complete</p>
          <p className="text-xs font-mono text-zinc-400">
            Created {import_summary.created}, updated {import_summary.upserted}, linked {import_summary.relations_linked} relations
            {import_summary.deferred > 0 ? `, ${import_summary.deferred} deferred` : ''}.
          </p>
        </div>
      )}
    </section>
  )
}

function SessionsSection({ sessions }) {
  const [open, setOpen] = useState(sessions.length > 0)

  return (
    <section className="space-y-2">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 text-sm font-mono font-semibold text-zinc-300 hover:text-zinc-100 transition-colors"
      >
        <span className={`text-xs transition-transform ${open ? 'rotate-90' : ''}`}>▶</span>
        Sessions ({sessions.length})
      </button>

      {open && (
        sessions.length === 0 ? (
          <p className="text-sm font-mono text-zinc-600 py-3">No sessions recorded.</p>
        ) : (
          <div className="border border-zinc-800 rounded overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="bg-zinc-800/60 text-zinc-400 text-left">
                  <th className="px-4 py-2 font-medium">Developer</th>
                  <th className="px-4 py-2 font-medium">Started</th>
                  <th className="px-4 py-2 font-medium">Ended</th>
                  <th className="px-4 py-2 font-medium text-center">Memories</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {sessions.map(s => (
                  <tr key={s.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-4 py-2 text-zinc-100">{s.developer}</td>
                    <td className="px-4 py-2 text-zinc-400">{formatDate(s.started_at)}</td>
                    <td className="px-4 py-2 text-zinc-400">
                      {s.ended_at ? (
                        formatDate(s.ended_at)
                      ) : (
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-emerald-900/50 text-emerald-400 font-mono">
                          open
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-zinc-400 text-center">{s.memory_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  )
}

function MemoriesSection({ project, memories }) {
  const [selected, setSelected] = useState(new Set())

  const allIds = memories.data.map(m => m.id)
  const allSelected = allIds.length > 0 && allIds.every(id => selected.has(id))

  function toggleAll() {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(allIds))
    }
  }

  function toggleOne(id) {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function handleDelete(id, title) {
    if (!confirm(`Delete memory '${title}'? This cannot be undone.`)) return
    router.delete(`/settings/projects/${project.slug}/memories/${id}`, {
      preserveScroll: true,
    })
  }

  function handleBulkDelete() {
    const count = selected.size
    if (!confirm(`Delete ${count} ${count === 1 ? 'memory' : 'memories'}? This cannot be undone.`)) return
    router.post(
      `/settings/projects/${project.slug}/bulk_destroy_memories`,
      { memory_ids: Array.from(selected) },
      { preserveScroll: true, onSuccess: () => setSelected(new Set()) }
    )
  }

  function goToPage(page) {
    setSelected(new Set())
    router.get(`/settings/projects/${project.slug}`, { page }, { preserveScroll: true })
  }

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-mono font-semibold text-zinc-300">
          Memories ({memories.total_count})
        </h2>
        {selected.size > 0 && (
          <button
            onClick={handleBulkDelete}
            className="px-3 py-1.5 rounded text-xs font-mono text-red-400 border border-red-900 hover:bg-red-950/60 hover:text-red-300 transition-colors"
          >
            Delete selected ({selected.size})
          </button>
        )}
      </div>

      {memories.data.length === 0 ? (
        <p className="text-sm font-mono text-zinc-600 py-3">No memories in this project.</p>
      ) : (
        <>
          <div className="border border-zinc-800 rounded overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="bg-zinc-800/60 text-zinc-400 text-left">
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      className="accent-emerald-500 cursor-pointer"
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-4 py-2 font-medium">Title</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Scope</th>
                  <th className="px-4 py-2 font-medium">Created</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {memories.data.map(m => (
                  <tr
                    key={m.id}
                    className={`hover:bg-zinc-800/30 transition-colors ${selected.has(m.id) ? 'bg-zinc-800/50' : ''}`}
                  >
                    <td className="px-3 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={selected.has(m.id)}
                        onChange={() => toggleOne(m.id)}
                        className="accent-emerald-500 cursor-pointer"
                        aria-label={`Select ${m.title}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-zinc-100">{m.title}</td>
                    <td className="px-4 py-2">
                      <TypeBadge type={m.memory_type} />
                    </td>
                    <td className="px-4 py-2 text-zinc-400">{m.scope}</td>
                    <td className="px-4 py-2 text-zinc-400">{formatDate(m.created_at)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleDelete(m.id, m.title)}
                        className="px-2 py-1 rounded text-xs text-red-400 border border-red-900 hover:bg-red-950/60 hover:text-red-300 transition-colors"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {memories.total_pages > 1 && (
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={() => goToPage(memories.current_page - 1)}
                disabled={memories.current_page <= 1}
                className="px-3 py-1.5 rounded text-xs font-mono bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Prev
              </button>
              <span className="text-xs font-mono text-zinc-500">
                Page {memories.current_page} of {memories.total_pages}
              </span>
              <button
                onClick={() => goToPage(memories.current_page + 1)}
                disabled={memories.current_page >= memories.total_pages}
                className="px-3 py-1.5 rounded text-xs font-mono bg-zinc-800 text-zinc-300 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}

export default function Show({
  project,
  memories,
  sessions = [],
  preflight_collisions,
  preflight_summary,
  import_summary,
}) {
  return (
    <Layout active="projects">
      <div className="max-w-4xl space-y-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm font-mono text-zinc-500">
          <Link
            href="/settings/projects"
            className="hover:text-emerald-400 transition-colors"
          >
            ← Projects
          </Link>
          <span className="text-zinc-700">/</span>
          <span className="text-zinc-300">{project.name}</span>
        </nav>

        <div className="flex items-center justify-between">
          <h1 className="text-lg font-mono font-bold text-zinc-100">{project.name}</h1>
          <ExportButton project={project} />
        </div>

        <ImportSection
          project={project}
          preflight_collisions={preflight_collisions}
          preflight_summary={preflight_summary}
          import_summary={import_summary}
        />

        <SessionsSection sessions={sessions} />

        <MemoriesSection project={project} memories={memories} />
      </div>
    </Layout>
  )
}
